import { useSelector } from "react-redux";
import {
  Grid,
  useTheme,
  Tooltip,
  Typography,
  Box,
  Button,
} from "@mui/material";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import GroupedSelectMultiple from "components/GroupedSelectMultiple";
import FormControlWrapper from "components/FormControlWrapper";
import DialogConfirmWrapper from "components/DialogConfirmWrapper";
import UndSDGoalsDialog from "./UndSDGoals.Dialog";
import SocialMediaDialog from "./SocialMediaIcons.Dialog";
import SubCard from "ui-component/cards/SubCard";
import icons from "./iconUndSDGoalsList";

import FacebookIcon from "@mui/icons-material/Facebook";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import InstagramIcon from "@mui/icons-material/Instagram";
import XIcon from "@mui/icons-material/X";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import { HelpOutline } from "@mui/icons-material";

const socialPlatforms = [
  { key: "facebook", icon: FacebookIcon, color: "#1877F2" },
  { key: "linkedin", icon: LinkedInIcon, color: "#0A66C2" },
  { key: "instagram", icon: InstagramIcon, color: "#E4405F" },
  { key: "X", icon: XIcon, color: "#000" },
];

const AdditionalInformationPopup = ({ openPopup, onClose }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { projectDescription } = useSelector((state) => state.userProjectState);

  const [categoryList, setCategoryList] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedPopulations, setSelectedPopulations] = useState([]);
  const [selectedGeographicAreas, setSelectedGeographicAreas] = useState([]);

  const [selectedGoals, setSelectedGoals] = useState([]);
  const [openGoalsDialog, setOpenGoalsDialog] = useState(false);

  const [selectedSocialMedia, setSelectedSocialMedia] = useState([]); // must be an array
  const [openSocialMediaDialog, setOpenSocialMediaDialog] = useState(false);
  const [showSocialMedia, setShowSocialMedia] = useState(false);
  const [socialMediaLinks, setSocialMediaLinks] = useState({});

  const categories = [
    // "Oliver Hansen",
    // "Van Henry",
    // "April Tucker",
    // "Ralph Hubbard",
    // "Omar Alexander",
    // "Carlos Abbott",
    // "Miriam Wagner",
    // "Bradley Wilkerson",
    // "Virginia Andrews",
    // "Kelly Snyder",
  ];

  useEffect(() => {
    const formattedCategories = categories.map((name, index) => ({
      id: index,
      value: name,
      label: name,
    }));
    setCategoryList(formattedCategories);
  }, []);

  const handleOpenGoalsDialog = () => setOpenGoalsDialog(true);
  const handleCloseGoalsDialog = () => setOpenGoalsDialog(false);
  const handleSaveGoals = (goals) => setSelectedGoals(goals);

  const handleOpenSocialMediaDialog = () => setOpenSocialMediaDialog(true);
  const handleCloseSocialMediaDialog = () => setOpenSocialMediaDialog(false);

  const handleSaveSocialMedia = (selectedIcons, links) => {
    setSelectedSocialMedia(selectedIcons); // updates the array used in your map
    setSocialMediaLinks(links); // optional: to use elsewhere
    setOpenSocialMediaDialog(false); // close the dialog
  };

  return (
    <DialogConfirmWrapper
      openPopup={openPopup}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: { width: "1400px", height: "1000px", maxWidth: "none" },
      }}
      titleMessage={t("labels.additional_information")}
      popupMessage={
        <>
          <Grid item xs={12} mt={1}>
            <SubCard title="General Information">
              <Grid container spacing={1}>
                <Grid item xs={4}>
                  <GroupedSelectMultiple
                    label={t("labels.sector")}
                    selected={selectedCategories}
                    list={categoryList}
                    onChange={(e) => setSelectedCategories(e.target.value)}
                    theme={theme}
                  />
                </Grid>
                <Grid item xs={4}>
                  <GroupedSelectMultiple
                    label={t("labels.populations_served")}
                    selected={selectedPopulations}
                    list={categoryList}
                    onChange={(e) => setSelectedPopulations(e.target.value)}
                    theme={theme}
                  />
                </Grid>
                <Grid item xs={4}>
                  <GroupedSelectMultiple
                    label={t("labels.geographic_areas_served")}
                    selected={selectedGeographicAreas}
                    list={categoryList}
                    onChange={(e) => setSelectedGeographicAreas(e.target.value)}
                    theme={theme}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2} mt={1}>
                <Grid item xs={12}>
                  <FormControlWrapper
                    name="website"
                    value={projectDescription.value}
                    label={t("labels.website")}
                    type="text"
                    maxLength="1000"
                    error={projectDescription.error}
                    theme={theme}
                    placeholder={"https://www.example.org"}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlWrapper
                    name="mission"
                    value={projectDescription.value}
                    label={t("labels.mission")}
                    type="text"
                    maxLength="4000"
                    error={projectDescription.error}
                    theme={theme}
                    multiline
                    rows={5}
                  />
                </Grid>
              </Grid>
            </SubCard>
          </Grid>

          {/* UND SDGs */}
          <Grid item xs={12} mt={1}>
            <SubCard
              title={
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Typography variant="h5">
                      {t("labels.un_sdg_goals")}
                    </Typography>
                    <Tooltip
                      title={t("messages.un_sdg_goals")}
                      sx={{ ml: 1, cursor: "pointer" }}
                    >
                      <HelpOutline fontSize="small" />
                    </Tooltip>
                  </Box>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleOpenGoalsDialog}
                  >
                    {t("labels.select_goals")}
                  </Button>
                </Box>
              }
            >
              <Box sx={{ mt: 2 }}>
                {selectedGoals.length > 0 ? (
                  <Grid container spacing={1}>
                    {selectedGoals.map((goalId) => {
                      const goal = icons.find((icon) => icon.id === goalId);
                      if (!goal) return null;

                      const handleRemove = () => {
                        setSelectedGoals((prev) =>
                          prev.filter((id) => id !== goalId)
                        );
                      };

                      return (
                        <Grid item key={goalId}>
                          <Box sx={{ position: "relative", width: 125 }}>
                            <img
                              src={goal.file}
                              alt={goal.name}
                              style={{ width: "100%", borderRadius: "7px" }}
                            />
                            <Tooltip title="Remove goal">
                              <IconButton
                                size="small"
                                onClick={handleRemove}
                                sx={{
                                  position: "absolute",
                                  top: -8,
                                  right: -8,
                                  backgroundColor: "#fff",
                                  border: "1px solid #ccc",
                                  padding: "2px",
                                  zIndex: 1,
                                  "&:hover": {
                                    backgroundColor: "#f0f0f0",
                                  },
                                }}
                              >
                                <CloseIcon fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    {t("labels.no_goals_selected")}
                  </Typography>
                )}
              </Box>
            </SubCard>
          </Grid>

          {/* Social Media */}
          <Grid item xs={12} mt={1}>
            <SubCard
              title={
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Typography variant="h5">
                      {t("labels.social_media")}
                    </Typography>
                    <Tooltip
                      title={t("messages.social_media")}
                      sx={{ ml: 1, cursor: "pointer" }}
                    >
                      <HelpOutline fontSize="small" />
                    </Tooltip>
                  </Box>

                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleOpenSocialMediaDialog}
                  >
                    {t("labels.select_social_media")}
                  </Button>
                </Box>
              }
            >
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  {Array.isArray(selectedSocialMedia) &&
                    selectedSocialMedia.map((key) => {
                      const platform = socialPlatforms.find(
                        (p) => p.key === key
                      );
                      if (!platform) return null;
                      const Icon = platform.icon;

                      const handleRemove = () => {
                        setSelectedSocialMedia((prev) =>
                          prev.filter((item) => item !== key)
                        );
                      };

                      return (
                        <Grid item key={key}>
                          <Box
                            sx={{ position: "relative", width: 65, height: 65 }}
                          >
                            <Icon
                              sx={{ color: platform.color, fontSize: 65 }}
                            />
                            <Tooltip
                              title={`Remove ${
                                key.charAt(0).toUpperCase() + key.slice(1)
                              }`}
                            >
                              <IconButton
                                size="small"
                                onClick={handleRemove}
                                sx={{
                                  position: "absolute",
                                  top: -10,
                                  right: -10,
                                  backgroundColor: "#fff",
                                  border: "1px solid #ccc",
                                  padding: "2px",
                                  zIndex: 1,
                                  "&:hover": {
                                    backgroundColor: "#f0f0f0",
                                  },
                                }}
                              >
                                <CloseIcon fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Grid>
                      );
                    })}
                </Grid>
              </Box>
            </SubCard>
          </Grid>

          {/* Dialogs */}
          <UndSDGoalsDialog
            open={openGoalsDialog}
            onClose={handleCloseGoalsDialog}
            onSave={handleSaveGoals}
            initialSelected={selectedGoals}
          />

          <SocialMediaDialog
            open={openSocialMediaDialog}
            onClose={handleCloseSocialMediaDialog}
            onConfirm={handleSaveSocialMedia}
            initialIcons={selectedSocialMedia}
            initialLinks={[]}
          />
        </>
      }
      confirmLabel={t("buttons.update")}
      additionalButtonLabel="Remind me Later"
      cancelLabel="Don't Show Again"
      onAdditionalButtonClick={(e) => {}}
    />
  );
};

export default AdditionalInformationPopup;
